import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { AccountShell } from '../components/account/AccountShell'
import { SettingsSection } from '../components/knowledge/settings/SettingsSection'
import { Button, Input, Modal, Text } from '../components/ui'
import { authClient } from '../lib/auth-client'

const deleteSchema = z
  .object({
    confirmText: z.string(),
    password: z.string().optional(),
  })
  .refine((data) => data.confirmText === 'DELETE', {
    message: 'Type DELETE to confirm',
    path: ['confirmText'],
  })

type DeleteForm = z.infer<typeof deleteSchema>

const privacyUrl = 'https://usesondersoft.com/privacy'
const termsUrl = 'https://usesondersoft.com/terms'

export const Route = createFileRoute('/data-and-privacy')({
  beforeLoad: async () => {
    const { data: session } = await authClient.getSession()
    if (!session) {
      throw redirect({ to: '/login' })
    }
    return { session }
  },
  component: DataAndPrivacyPage,
})

function DataAndPrivacyPage() {
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [hasCredentialAccount, setHasCredentialAccount] = useState<boolean | null>(null)

  const deleteForm = useForm<DeleteForm>({
    resolver: zodResolver(deleteSchema),
    defaultValues: {
      confirmText: '',
      password: '',
    },
  })

  useEffect(() => {
    let cancelled = false

    async function loadAccounts() {
      const { data } = await authClient.listAccounts()
      if (cancelled) return
      setHasCredentialAccount(
        data?.some((account) => account.providerId === 'credential') ?? false,
      )
    }

    void loadAccounts()

    return () => {
      cancelled = true
    }
  }, [])

  function openDeleteModal() {
    setDeleteError(null)
    deleteForm.reset()
    setDeleteOpen(true)
  }

  function closeDeleteModal() {
    setDeleteOpen(false)
    setDeleteError(null)
    deleteForm.reset()
  }

  async function onDeleteSubmit(data: DeleteForm) {
    setDeleteError(null)

    const { error } = await authClient.deleteUser(
      data.password ? { password: data.password } : {},
    )

    if (error) {
      setDeleteError(error.message ?? 'Could not delete your account')
      return
    }

    window.location.href = '/signup'
  }

  return (
    <AccountShell>
      <div className="mx-auto w-full max-w-2xl">
        <div className="mb-8">
          <Text variant="h2" className="mb-1 text-2xl sm:text-3xl">
            Data and privacy
          </Text>
          <Text variant="body-sm" className="text-foreground/60">
            Learn how SonderSoft handles your data and account.
          </Text>
        </div>

        <SettingsSection
          id="policies"
          title="Policies"
          description="How we collect, use, and protect your information."
        >
          <div className="flex flex-col gap-3 text-sm">
            <a
              href={privacyUrl}
              target="_blank"
              rel="noreferrer"
              className="w-fit font-medium text-foreground underline-offset-4 hover:underline"
            >
              Privacy Policy
            </a>
            <a
              href={termsUrl}
              target="_blank"
              rel="noreferrer"
              className="w-fit font-medium text-foreground underline-offset-4 hover:underline"
            >
              Terms of Service
            </a>
          </div>
        </SettingsSection>

        <SettingsSection
          id="your-data"
          title="Your data"
          description="What SonderSoft stores for your account."
        >
          <ul className="list-disc space-y-2 pl-5 text-sm text-foreground/70">
            <li>Account details such as your name and email address.</li>
            <li>Workspace membership, roles, and content you create in SonderSoft Knowledge.</li>
            <li>Session and authentication records needed to keep you signed in securely.</li>
          </ul>
          <Text variant="caption" className="mt-4 block text-foreground/45">
            To request a copy of your data, contact{' '}
            <a href="mailto:hello@usesondersoft.com" className="underline-offset-4 hover:underline">
              hello@usesondersoft.com
            </a>
            .
          </Text>
        </SettingsSection>

        <SettingsSection
          id="delete-account"
          title="Delete account"
          description="Permanently remove your SonderSoft account and workspace memberships."
        >
          <div className="rounded-2xl border border-red-200 bg-red-50/60 p-5">
            <Text variant="label" className="mb-2 text-red-900">
              This action cannot be undone
            </Text>
            <Text variant="body-sm" className="mb-4 text-red-900/80">
              Your account will be deleted. Workspace content you created will remain with the
              workspace. If you are the only admin of a workspace, invite another admin first.
            </Text>
            <Button type="button" variant="outline" onClick={openDeleteModal}>
              Delete my account
            </Button>
          </div>
        </SettingsSection>
      </div>

      <Modal open={deleteOpen} onClose={closeDeleteModal}>
        <Text variant="h2" className="mb-2 pr-8 text-xl">
          Delete account
        </Text>
        <Text variant="body-sm" className="mb-6 text-foreground/60">
          This permanently deletes your SonderSoft account. Type DELETE to confirm.
        </Text>

        <form className="flex flex-col gap-4" onSubmit={deleteForm.handleSubmit(onDeleteSubmit)}>
          <Input
            label='Type "DELETE" to confirm'
            autoComplete="off"
            error={deleteForm.formState.errors.confirmText?.message}
            {...deleteForm.register('confirmText')}
          />

          {hasCredentialAccount && (
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              error={deleteForm.formState.errors.password?.message}
              {...deleteForm.register('password', {
                required: hasCredentialAccount ? 'Password is required' : false,
              })}
            />
          )}

          {deleteError && (
            <Text variant="caption" className="text-red-600">
              {deleteError}
            </Text>
          )}

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={closeDeleteModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="outline"
              fullWidth
              loading={deleteForm.formState.isSubmitting}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              Delete account
            </Button>
          </div>
        </form>
      </Modal>
    </AccountShell>
  )
}
